import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  Book,
  MagnifyingGlass,
  CaretRight,
  CaretLeft,
  ArrowLeft,
  BookOpen,
  Warning,
  Gavel,
  Image as ImageIcon,
  SpinnerGap,
  X
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section icons based on content type
const SECTION_ICONS = {
  'introduction': '📖',
  'pedestrians-1-to-35': '🚶',
  'powered-wheelchairs-36-to-46': '♿',
  'animals-47-to-58': '🐴',
  'cyclists-59-to-82': '🚴',
  'motorcyclists-83-to-88': '🏍️',
  'drivers-motorcyclists-89-to-102': '🚗',
  'general-rules-103-to-158': '📋',
  'using-the-road-159-to-203': '🛣️',
  'extra-care-204-to-225': '⚠️',
  'adverse-weather-226-to-237': '🌧️',
  'waiting-parking-238-to-252': '🅿️',
  'motorways-253-to-274': '🛤️',
  'breakdowns-275-to-287': '🔧',
  'road-works-288-to-307': '🚧',
  'light-signals': '🚦',
  'signals-to-other-road-users': '👋',
  'signals-by-authorised-persons': '👮',
  'traffic-signs': '🪧',
  'road-markings': '🖊️',
  'vehicle-markings': '🚛',
  'annex-1-bicycle': '🚲',
  'annex-2-motorcycle': '🏍️',
  'annex-3-documents': '📄',
  'annex-4-law': '⚖️',
  'annex-5-penalties': '🚨',
  'annex-6-vehicle-maintenance': '🔩',
  'annex-7-first-aid': '🩹',
  'annex-8-new-drivers': '🎓',
};

const SECTION_GROUPS = [
  { label: 'Rules', ids: ['introduction', 'pedestrians-1-to-35', 'powered-wheelchairs-36-to-46', 'animals-47-to-58', 'cyclists-59-to-82', 'motorcyclists-83-to-88', 'drivers-motorcyclists-89-to-102', 'general-rules-103-to-158', 'using-the-road-159-to-203', 'extra-care-204-to-225', 'adverse-weather-226-to-237', 'waiting-parking-238-to-252', 'motorways-253-to-274', 'breakdowns-275-to-287', 'road-works-288-to-307'] },
  { label: 'Signals & Signs', ids: ['light-signals', 'signals-to-other-road-users', 'signals-by-authorised-persons', 'traffic-signs', 'road-markings', 'vehicle-markings'] },
  { label: 'Annexes', ids: ['annex-1-bicycle', 'annex-2-motorcycle', 'annex-3-documents', 'annex-4-law', 'annex-5-penalties', 'annex-6-vehicle-maintenance', 'annex-7-first-aid', 'annex-8-new-drivers'] },
];


const RuleContent = ({ rule }) => {
  const [imgError, setImgError] = useState({});

  // Format content with bold/MUST highlighting
  const formatContent = (text) => {
    if (!text) return null;
    return text.split('\n\n').map((para, i) => {
      // Check if this is a list
      if (para.startsWith('- ')) {
        const items = para.split('\n').filter(l => l.startsWith('- '));
        return (
          <ul key={i} className="list-disc list-outside ml-5 space-y-1 text-sm text-muted-foreground">
            {items.map((item, j) => (
              <li key={j} dangerouslySetInnerHTML={{ __html: highlightMust(item.slice(2)) }} />
            ))}
          </ul>
        );
      }
      if (/^\d+\.\s/.test(para)) {
        const items = para.split('\n').filter(l => /^\d+\.\s/.test(l));
        return (
          <ol key={i} className="list-decimal list-outside ml-5 space-y-1 text-sm text-muted-foreground">
            {items.map((item, j) => (
              <li key={j} dangerouslySetInnerHTML={{ __html: highlightMust(item.replace(/^\d+\.\s/, '')) }} />
            ))}
          </ol>
        );
      }
      // Check for table-like content (pipes)
      if (para.includes(' | ')) {
        const rows = para.split('\n');
        return (
          <div key={i} className="overflow-x-auto my-2">
            <table className="text-xs border border-border w-full">
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri} className={ri === 0 ? "bg-muted font-bold" : ""}>
                    {row.split(' | ').map((cell, ci) => (
                      <td key={ci} className="border border-border px-2 py-1">{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      return (
        <p key={i} className="text-sm text-muted-foreground leading-relaxed"
           dangerouslySetInnerHTML={{ __html: highlightMust(para) }} />
      );
    });
  };

  const highlightMust = (text) => {
    return text
      .replace(/\bMUST NOT\b/g, '<strong class="text-destructive font-bold">MUST NOT</strong>')
      .replace(/\bMUST\b/g, '<strong class="text-destructive font-bold">MUST</strong>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  };

  return (
    <div className="space-y-3" data-testid={`rule-${rule.rule_id}`}>
      {/* Content */}
      <div className="space-y-2">
        {formatContent(rule.content)}
      </div>

      {/* Images */}
      {rule.images && rule.images.length > 0 && (
        <div className="space-y-3 mt-3">
          {rule.images.map((img, i) => (
            !imgError[`${rule.rule_id}-${i}`] && (
              <figure key={i} className="border border-border rounded-sm overflow-hidden bg-muted/30">
                <img
                  src={img.url}
                  alt={img.caption || `Rule ${rule.rule_id} illustration`}
                  className="w-full h-auto object-contain max-h-80"
                  loading="lazy"
                  onError={() => setImgError(prev => ({ ...prev, [`${rule.rule_id}-${i}`]: true }))}
                  data-testid={`rule-image-${rule.rule_id}-${i}`}
                />
                {img.caption && (
                  <figcaption className="px-3 py-2 text-xs text-muted-foreground italic border-t border-border bg-card">
                    {img.caption}
                  </figcaption>
                )}
              </figure>
            )
          ))}
        </div>
      )}

      {/* Laws */}
      {rule.laws && (
        <div className="flex items-start gap-2 mt-2 p-2 bg-muted/30 rounded-sm border border-border/50">
          <Gavel weight="fill" className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-muted-foreground leading-relaxed">{rule.laws}</p>
        </div>
      )}
    </div>
  );
};


const SectionView = ({ section, rules, onBack, questionCounts }) => {
  const [expandedRule, setExpandedRule] = useState(null);
  const [showAllRules, setShowAllRules] = useState(false);
  const rulesRef = useRef(null);
  const navigate = useNavigate();

  // Group rules by subsection
  const groupedRules = {};
  rules.forEach(rule => {
    const sub = rule.subsection || 'General';
    if (!groupedRules[sub]) groupedRules[sub] = [];
    groupedRules[sub].push(rule);
  });

  const subsections = Object.keys(groupedRules);
  const totalRules = rules.length;
  const rulesWithImages = rules.filter(r => r.images && r.images.length > 0).length;

  return (
    <div data-testid="section-view">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3 transition-colors"
          data-testid="back-to-sections"
        >
          <ArrowLeft weight="bold" className="w-4 h-4" />
          All Sections
        </button>
        <div className="flex items-start gap-3">
          <span className="text-2xl">{SECTION_ICONS[section.section_id] || '📖'}</span>
          <div>
            <h1 className="font-heading font-black text-xl md:text-2xl tracking-tight">{section.title}</h1>
            <p className="text-sm text-muted-foreground mt-1">{section.description}</p>
            <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <BookOpen className="w-3.5 h-3.5" /> {totalRules} rules
              </span>
              {rulesWithImages > 0 && (
                <span className="flex items-center gap-1">
                  <ImageIcon className="w-3.5 h-3.5" /> {rulesWithImages} with images
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Rules */}
      <div className="space-y-3" ref={rulesRef}>
        {subsections.map((sub, si) => (
          <div key={si}>
            {subsections.length > 1 && sub !== 'General' && (
              <h2 className="font-heading font-bold text-sm text-foreground mb-2 mt-4 border-b border-border pb-1">
                {sub}
              </h2>
            )}
            {groupedRules[sub].map((rule) => {
              const isExpanded = expandedRule === rule.rule_id || showAllRules;
              const ruleLabel = /^(Rule\s)?(H?\d+)/.test(rule.title)
                ? rule.title
                : rule.title;

              return (
                <div
                  key={rule.rule_id}
                  className={cn(
                    "border rounded-sm transition-all",
                    isExpanded ? "border-accent/40 bg-accent/5" : "border-border bg-card hover:border-accent/20"
                  )}
                >
                  <button
                    onClick={() => {
                      if (showAllRules) return;
                      setExpandedRule(expandedRule === rule.rule_id ? null : rule.rule_id);
                    }}
                    className="w-full text-left px-4 py-3 flex items-center justify-between"
                    data-testid={`rule-toggle-${rule.rule_id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-mono font-bold text-accent bg-accent/10 px-1.5 py-0.5 rounded-sm flex-shrink-0">
                        {/^(Rule\s)?(H?\d+)/.test(rule.title)
                          ? rule.title.match(/(H?\d+)/)?.[0] || rule.rule_id
                          : rule.rule_id.startsWith('img-') ? '★' : rule.rule_id.slice(0, 6)}
                      </span>
                      <span className="text-sm font-medium truncate">{ruleLabel}</span>
                      {rule.images && rule.images.length > 0 && (
                        <ImageIcon weight="fill" className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                      )}
                      {questionCounts?.[rule.rule_id] > 0 && (
                        <span className="text-[10px] font-mono text-success bg-success/10 px-1.5 py-0.5 rounded-sm flex-shrink-0" data-testid={`qcount-${rule.rule_id}`}>
                          {questionCounts[rule.rule_id]}Q
                        </span>
                      )}
                    </div>
                    {!showAllRules && (
                      <CaretRight weight="bold" className={cn(
                        "w-4 h-4 text-muted-foreground transition-transform flex-shrink-0",
                        isExpanded && "rotate-90"
                      )} />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-border/50">
                      <div className="pt-3">
                        <RuleContent rule={rule} />
                        {questionCounts?.[rule.rule_id] > 0 && (
                          <button
                            onClick={() => navigate(`/practice/rule/${section.section_id}/${rule.rule_id}`)}
                            className="mt-3 flex items-center gap-2 text-xs font-bold text-accent bg-accent/10 hover:bg-accent/20 border border-accent/30 px-3 py-1.5 rounded-sm transition-colors"
                            data-testid={`practice-rule-${rule.rule_id}`}
                          >
                            <BookOpen className="w-3.5 h-3.5" />
                            Practice {questionCounts[rule.rule_id]} related question{questionCounts[rule.rule_id] > 1 ? 's' : ''}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Expand/Collapse all */}
      {totalRules > 3 && (
        <div className="mt-4 text-center">
          <button
            onClick={() => { setShowAllRules(!showAllRules); setExpandedRule(null); }}
            className="text-xs text-accent hover:text-accent/80 font-medium"
            data-testid="toggle-all-rules"
          >
            {showAllRules ? 'Collapse all rules' : 'Expand all rules'}
          </button>
        </div>
      )}
    </div>
  );
};


const SearchResults = ({ results, onSelectSection, onClose }) => {
  if (!results || results.length === 0) return (
    <div className="text-center py-8 text-muted-foreground text-sm">
      No matching rules found.
    </div>
  );

  // Group by section
  const grouped = {};
  results.forEach(r => {
    if (!grouped[r.section_id]) grouped[r.section_id] = [];
    grouped[r.section_id].push(r);
  });

  return (
    <div data-testid="search-results">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">{results.length} results found</p>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
          <X weight="bold" className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-4">
        {Object.entries(grouped).map(([sectionId, rules]) => (
          <div key={sectionId}>
            <button
              onClick={() => onSelectSection(sectionId)}
              className="text-xs font-bold text-accent hover:underline mb-2 block"
            >
              {SECTION_ICONS[sectionId] || '📖'} {sectionId.replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase())}
            </button>
            <div className="space-y-2">
              {rules.slice(0, 5).map((rule, i) => (
                <div key={i} className="border border-border rounded-sm p-3 bg-card">
                  <p className="text-sm font-medium mb-1">{rule.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{rule.content}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


export const HighwayCodePage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [sectionRules, setSectionRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRules, setLoadingRules] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [questionCounts, setQuestionCounts] = useState({});
  const searchTimeout = useRef(null);

  useEffect(() => {
    fetchSections();
  }, []);

  useEffect(() => {
    const sid = searchParams.get('section');
    if (sid && sections.length > 0) {
      loadSection(sid);
    }
  }, [sections, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSections = async () => {
    try {
      const { data } = await axios.get(`${API}/highway-code/sections`, { withCredentials: true });
      setSections(data.sections || []);
    } catch (err) {
      console.error('Failed to load Highway Code sections:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSection = async (sectionId) => {
    setLoadingRules(true);
    setSearchResults(null);
    try {
      const [sectionRes, countsRes] = await Promise.all([
        axios.get(`${API}/highway-code/sections/${sectionId}`, { withCredentials: true }),
        axios.get(`${API}/highway-code/sections/${sectionId}/question-counts`, { withCredentials: true }).catch(() => ({ data: { counts: {} } })),
      ]);
      setSelectedSection(sectionRes.data.section);
      setSectionRules(sectionRes.data.rules || []);
      setQuestionCounts(countsRes.data.counts || {});
      setSearchParams({ section: sectionId }, { replace: true });
    } catch (err) {
      console.error('Failed to load section:', err);
    } finally {
      setLoadingRules(false);
    }
  };

  const handleBack = () => {
    setSelectedSection(null);
    setSectionRules([]);
    setSearchParams({}, { replace: true });
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (!query || query.length < 2) {
      setSearchResults(null);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/highway-code/search`, {
          params: { q: query },
          withCredentials: true
        });
        setSearchResults(data.results || []);
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setSearching(false);
      }
    }, 400);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <SpinnerGap className="w-8 h-8 text-accent animate-spin" />
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8" data-testid="highway-code-page">
        <div className="max-w-3xl mx-auto text-center py-16">
          <Book weight="fill" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="font-heading font-black text-2xl mb-2">Highway Code</h1>
          <p className="text-muted-foreground mb-4">
            The Highway Code content hasn't been loaded yet. An admin needs to seed the data first.
          </p>
          {user?.role === 'admin' && (
            <button
              onClick={async () => {
                try {
                  await axios.post(`${API}/admin/highway-code/seed`, {}, { withCredentials: true });
                  alert('Scraping started! Check back in a minute.');
                } catch (e) {
                  alert('Failed to start scraping');
                }
              }}
              className="bg-accent text-accent-foreground px-4 py-2 rounded-sm text-sm font-bold"
              data-testid="seed-highway-code"
            >
              Seed Highway Code Data
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="highway-code-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        {!selectedSection && (
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <Book weight="fill" className="w-6 h-6 text-accent" />
              <h1 className="font-heading font-black text-2xl md:text-3xl tracking-tight">
                The Highway Code
              </h1>
            </div>
            <p className="text-sm text-muted-foreground">
              Official DVSA Highway Code rules and guidance for all road users in England, Scotland and Wales.
            </p>
          </div>
        )}

        {/* Search */}
        {!selectedSection && (
          <div className="relative mb-6">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search rules (e.g. 'zebra crossing', 'speed limit', 'roundabout')"
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              className="w-full bg-card border border-border rounded-sm pl-9 pr-4 py-2.5 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-accent"
              data-testid="highway-code-search"
            />
            {searching && (
              <SpinnerGap className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-accent animate-spin" />
            )}
          </div>
        )}

        {/* Search Results */}
        {searchResults !== null && !selectedSection && (
          <SearchResults
            results={searchResults}
            onSelectSection={loadSection}
            onClose={() => { setSearchResults(null); setSearchQuery(''); }}
          />
        )}

        {/* Section List */}
        {!selectedSection && searchResults === null && (
          <div className="space-y-6" data-testid="sections-list">
            {SECTION_GROUPS.map(group => {
              const groupSections = sections.filter(s => group.ids.includes(s.section_id));
              if (groupSections.length === 0) return null;
              return (
                <div key={group.label}>
                  <h2 className="font-heading font-bold text-xs uppercase tracking-widest text-muted-foreground mb-2">
                    {group.label}
                  </h2>
                  <div className="space-y-1.5">
                    {groupSections.map(section => (
                      <button
                        key={section.section_id}
                        onClick={() => loadSection(section.section_id)}
                        className="w-full text-left bg-card border border-border rounded-sm p-3 hover:border-accent/30 transition-colors flex items-center gap-3 group"
                        data-testid={`section-${section.section_id}`}
                      >
                        <span className="text-lg flex-shrink-0">
                          {SECTION_ICONS[section.section_id] || '📖'}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium group-hover:text-accent transition-colors truncate">
                            {section.title}
                          </p>
                          <p className="text-[11px] text-muted-foreground truncate">
                            {section.description}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded-sm">
                            {section.rules_count}
                          </span>
                          <CaretRight weight="bold" className="w-4 h-4 text-muted-foreground group-hover:text-accent transition-colors" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Loading Rules */}
        {loadingRules && (
          <div className="flex items-center justify-center py-16">
            <SpinnerGap className="w-8 h-8 text-accent animate-spin" />
          </div>
        )}

        {/* Section Detail View */}
        {selectedSection && !loadingRules && (
          <SectionView
            section={selectedSection}
            rules={sectionRules}
            onBack={handleBack}
            questionCounts={questionCounts}
          />
        )}
      </div>
    </div>
  );
};
